import { format, isToday, isTomorrow, differenceInCalendarDays } from "date-fns";
import { AccessResource } from "src/types/accessControl";

interface GetDiscountProps {
  discount: number;
  discountType: "percentage" | "amount";
}

export const capitalize = (value?: string, splitSnakeCase?: boolean) => {
  if (!value) return "";

  let convertedValue = value;

  if (splitSnakeCase) {
    convertedValue = convertedValue.split("_").join(" ");
  }

  return convertedValue
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

export const numberWithCommas = (value?: number | string | null) => {
  if (value === undefined || value === null) return "0";

  return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export const getCurrency = (value?: number | string) => {
  if (value === undefined) return "";

  return `Rp ${numberWithCommas(value).replace(/,/g, ".")}`;
};

export const getDiscount = ({ discount, discountType }: GetDiscountProps) => {
  let promotionDiscount = "";

  if (discountType === "amount") {
    promotionDiscount = getCurrency(discount);
  } else {
    promotionDiscount = `${discount}%`;
  }

  return promotionDiscount;
};

export const roundUpToNearest500 = (amount: number) => {
  return Math.ceil(amount / 500) * 500;
}

export const AccessResourceTitleMap: Record<string, string> = Object.entries(
  AccessResource
).reduce((acc, [key, value]) => {
  acc[value] = key;
  return acc;
}, {} as Record<string, string>);

export function getBirthdayLabel(birthDate: Date) {
  const today = new Date();
  
  // 1. Create a copy of the birthdate and set it to the current year
  let targetBirthday = new Date(birthDate);
  targetBirthday.setFullYear(today.getFullYear());

  // 2. Handle Feb 29 simplification (Force to Feb 28)
  if (birthDate.getMonth() === 1 && birthDate.getDate() === 29) {
    targetBirthday.setDate(28);
  }

  // 3. Calculate the difference
  let daysDiff = differenceInCalendarDays(targetBirthday, today);

  // 4. Check for Year End Rollover
  // If the difference is negative, the birthday has passed this year.
  // We must shift the target to next year to check if it's coming up soon.
  if (daysDiff < 0) {
    targetBirthday.setFullYear(today.getFullYear() + 1);
    daysDiff = differenceInCalendarDays(targetBirthday, today);
  }

  // 5. Check if it falls within the 7-day window
  if (daysDiff < 0 || daysDiff > 7) return undefined;

  const formattedDate = format(targetBirthday, 'dd MMM');

  if (daysDiff === 0) {
    return `Having a birthday (${formattedDate})`;
  }

  if (daysDiff === 1) {
    return `Having a birthday tomorrow (${formattedDate})`;
  }

  return `Upcoming birthday in ${daysDiff} days (${formattedDate})`;
}